import React from 'react';
import MediaBreakpoints from 'Utils/MediaBreakpoints';
import CurrencyLabel from 'Utils/CurrencyLabel';
import { DISCOUNT_DISPLAY_MODES } from 'Constants/discountDisplay';

const AGGREGATED_CONFIG = [{ kind: 'inventoryTotal', label: 'Inventory Total' }];

const ITEMIZED_CONFIG = [
  { kind: 'rentalItemTotal', label: 'Rental Item Total' },
  { kind: 'purchaseTotal', label: 'Purchase Total' },
  { kind: 'bundleTotal', label: 'Bundle Total' },
];

// Mirrors the admin "Inventory Discounts & Fees" table
// (display/InventoryDiscountsFeesTable): one row per inventory-total billing
// line that has a discount and/or fee. Rendered as a <div> inside the existing
// `section.items` (alongside the section divs) so it inherits the same themed
// CSS — a second top-level `section.items` collides with the theme styling.
const InventoryDiscountsFeesTable = ({
  billingLines,
  discountDisplay,
  displayAggregatedInventoryPrices = false,
}) => {
  if (!Array.isArray(billingLines) || billingLines.length === 0) return null;

  const configs = displayAggregatedInventoryPrices
    ? AGGREGATED_CONFIG
    : ITEMIZED_CONFIG;

  const rows = configs
    .map((config) => {
      const line = billingLines.find((bl) => bl.kind === config.kind);
      if (!line) return null;
      const discountValue = line.discountValue || 0;
      const feeValue = line.feeValue || 0;
      if (discountValue === 0 && feeValue === 0) return null;
      return { ...config, line, discountValue, feeValue };
    })
    .filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <div className='inventoryDiscountsFees'>
      <h4>Inventory Discounts &amp; Fees</h4>
      {discountDisplay === DISCOUNT_DISPLAY_MODES.SUMMARY_ONLY && (
        <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 'bold' }}>
          * Discounts and Fees are included in subtotals
        </p>
      )}
      <MediaBreakpoints
        desktop={
          <table className='grid'>
            <thead>
              <tr>
                <th>Name</th>
                <th className='right'>Listed Price</th>
                <th className='right'>Discount Total</th>
                <th className='right'>Fee Total</th>
                <th className='right'>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.kind}>
                  <td>{row.label}</td>
                  <td className='right'>
                    <CurrencyLabel value={row.line.baseValue || 0} />
                  </td>
                  <td className='right'>
                    <CurrencyLabel value={row.discountValue} />
                  </td>
                  <td className='right'>
                    <CurrencyLabel value={row.feeValue} />
                  </td>
                  <td className='right'>
                    <CurrencyLabel value={row.line.finalValue || 0} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
        mobile={
          <div className='invFeesLines'>
            {rows.map((row) => (
              <div className='invFeesLine' key={row.kind}>
                <div className='invFeesLine-header'>{row.label}</div>
                <div className='invFeesLine-detail'>
                  <div className='invFeesLine-title'>Listed Price</div>
                  <div className='invFeesLine-value'>
                    <CurrencyLabel value={row.line.baseValue || 0} />
                  </div>
                </div>
                <div className='invFeesLine-detail'>
                  <div className='invFeesLine-title'>Discount Total</div>
                  <div className='invFeesLine-value'>
                    <CurrencyLabel value={row.discountValue} />
                  </div>
                </div>
                <div className='invFeesLine-detail'>
                  <div className='invFeesLine-title'>Fee Total</div>
                  <div className='invFeesLine-value'>
                    <CurrencyLabel value={row.feeValue} />
                  </div>
                </div>
                <div className='invFeesLine-detail'>
                  <div className='invFeesLine-title'>Total</div>
                  <div className='invFeesLine-value'>
                    <CurrencyLabel value={row.line.finalValue || 0} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      />
    </div>
  );
};

export default InventoryDiscountsFeesTable;
